import CourtType from "../enums/court-type";
import GameType from "../enums/game-type";
import ScoringMode from "../enums/scoring-mode";
import TiebreakType from "../enums/tiebreak-type";
import MatchTiebreakType from "../enums/match-tiebreak-type";
import { resolvePlayerName } from "../../common/utils/player-utils";
import { isDefined } from "../../../services/common/object-util";
import {
    formatDateInTimeZone,
    formatTimeInTimeZone,
    isBefore7PM
} from "../../../services/common/date-util";
import { TeamSerializer } from "../../../services/serializers/legacy/tennis/TeamSerializer";

export default class BoxScore {
    constructor({
        gameContext = {},
        event = {},
        matches = [],
        homeTeam,
        homePlayers,
        awayTeam,
        awayPlayers
    }) {
        this.gameContext = gameContext;
        this.event = event;
        this.matches = matches;
        this.homeTeam = homeTeam;
        this.awayTeam = awayTeam;
        this.homePlayers = homePlayers;
        this.awayPlayers = awayPlayers;
    }

    calculate() {
        const singleMatches = this.matches.filter((match) => match.isSingle);
        const doubleMatches = this.matches.filter((match) => !match.isSingle);
        const {
            finalizeSettings,
            teamContexts,
            homeTeamId,
            awayTeamId,
            gameConfig,
            score
        } = this.gameContext;

        this.boxscore = {
            company: "PrestoSports",
            eventInfo: { id: this.event.eventId },
            date: new Date(),
            venue: this.getVenue(this.event, finalizeSettings),
            officials: this.event.personnel?.map((p) => ({
                name: p.fullName,
                role: p.role
            })),
            rules: this.getRules(gameConfig),
            notes: this.event.notes,
            teams: {
                home: TeamSerializer.serialize(
                    this.homeTeam,
                    this.homePlayers,
                    singleMatches,
                    doubleMatches,
                    teamContexts[homeTeamId],
                    score[0],
                    "home"
                ),
                away: TeamSerializer.serialize(
                    this.awayTeam,
                    this.awayPlayers,
                    singleMatches,
                    doubleMatches,
                    teamContexts[awayTeamId],
                    score[1],
                    "away"
                )
            },
            singles: this.getMatchesData(
                singleMatches,
                this.gameContext,
                this.awayPlayers,
                this.homePlayers
            ),
            doubles: this.getMatchesData(
                doubleMatches,
                this.gameContext,
                this.awayPlayers,
                this.homePlayers
            )
        };
    }

    getMatchesData(matches, gameContext, awayPlayers, homePlayers) {
        const { singles, doubles } = gameContext.finalizeSettings.finalizeOrder;
        return matches.map((match) => {
            const {
                isSingle,
                matchTiebreakType,
                tiebreakSets,
                tiebreakPoints,
                duration
            } = match;

            const matchTiebreak =
                matchTiebreakType === MatchTiebreakType.Points.code
                    ? {
                          type: "pts",
                          data: tiebreakPoints
                      }
                    : matchTiebreakType === MatchTiebreakType.Sets.code
                    ? { type: "sets", data: tiebreakSets }
                    : null;

            const order = isSingle ? singles : doubles;
            return {
                match: match.matchNumber,
                matchTiebreak: matchTiebreak?.type || null,
                order: order.findIndex((order) => order === match.id),
                asflight: "",
                touropp: "",
                duration,
                scores: {
                    away: this.getMatchScores(
                        isSingle,
                        match.sets,
                        matchTiebreak,
                        awayPlayers
                            .filter((p) =>
                                match.away.players.some((mpId) => mpId === p.id)
                            )
                            .sort((a, b) => a.uniform - b.uniform),
                        "away"
                    ),
                    home: this.getMatchScores(
                        isSingle,
                        match.sets,
                        matchTiebreak,
                        homePlayers
                            .filter((p) =>
                                match.home.players.some((mpId) => mpId === p.id)
                            )
                            .sort((a, b) => a.uniform - b.uniform),
                        "home"
                    )
                }
            };
        });
    }

    getSetData(sets, teamKey, prefix) {
        const set = {};
        sets.forEach((s) => {
            // A set nobody scored in was never played; both teams must skip it
            // so home/away serialize the same set_N attributes
            if (!isDefined(s.home.score) && !isDefined(s.away.score)) {
                return;
            }
            const { tiebreak, score } = s[teamKey];
            const i = `${prefix}${Object.keys(set).length + 1}`;
            set[i] = score || 0;
            if (tiebreak !== null && tiebreak !== undefined) {
                const j = `${prefix}${Object.keys(set).length + 1}`;
                set[j] = s[teamKey].tiebreak || 0;
            }
        });
        return set;
    }

    /**
     * Retrieve a formatted version of the provided sets for a given team using a prefix and tiePrefix
     * @param {{home: {score: number, tiebreak: number}, away: {score: number, tiebreak: number}}[]} sets
     * @param {"home" | "away"} teamKey
     * @param {string} prefix
     * @param {string} tiePrefix
     * @returns {{[key: string]: number}}
     */
    getEnhancedSetData(sets, teamKey, prefix, tiePrefix) {
        const set = {};
        sets.forEach((s, idx) => {
            const index = idx + 1;
            const { tiebreak, score } = s[teamKey];
            const i = `${prefix}${index}`;

            if (isDefined(score)) {
                set[i] = score;
            }

            if (isDefined(tiebreak)) {
                const j = `${tiePrefix}${index}`;
                set[j] = tiebreak;
            }
        });
        return set;
    }

    getMatchScores(isSingle, sets, matchTiebreak, participants, key) {
        const { type: matchTiebreakType, data: matchTiebreakData } =
            matchTiebreak || {};
        let uniform;
        let name;
        if (participants.length) {
            uniform = isSingle
                ? { uni: TeamSerializer.getUniform(participants[0]) }
                : {
                      uni_1: TeamSerializer.getUniform(participants[0]),
                      uni_2: participants[1]
                          ? TeamSerializer.getUniform(participants[1])
                          : ""
                  };
            name = isSingle
                ? {
                      name: resolvePlayerName(participants[0])
                  }
                : {
                      name_1: resolvePlayerName(participants[0]),
                      name_2: participants[1]
                          ? resolvePlayerName(participants[1])
                          : ""
                  };
        }

        let tiebreak = {};
        let altTiebreak = {};
        if (matchTiebreakType === "pts" && matchTiebreakData) {
            tiebreak = { tiebreakScore: matchTiebreakData[key] };
        } else if (matchTiebreakType === "sets") {
            tiebreak = this.getSetData(matchTiebreakData, key, "tie_");
            altTiebreak = this.getEnhancedSetData(
                matchTiebreakData,
                key,
                "match_tb_set_",
                "match_tb_tie_"
            );
        }

        return {
            vh: key === "away" ? "V" : "H",
            ...uniform,
            ...name,
            ...this.getSetData(sets, key, "set_"),
            ...tiebreak,
            ...this.getEnhancedSetData(sets, key, "game_set_", "game_tie_"),
            ...altTiebreak
        };
    }

    getVenue(event, finalizeSettings) {
        const {
            eventId,
            teams,
            startDateTime,
            venue,
            city,
            eventType,
            notes,
            status,
            statusCode
        } = event;
        const { attendance, endTime, duration } = finalizeSettings;
        const { isRegional, isNeutral, isPostSeason, isConference } =
            eventType || {};
        const { awayTeam, homeTeam } = teams;

        let formattedEndTime = "";
        if (endTime) {
            formattedEndTime = formatTimeInTimeZone(endTime);
        }

        const date = formatDateInTimeZone(startDateTime);
        const time = formatTimeInTimeZone(startDateTime);

        return {
            status,
            statusCode,
            gameId: eventId,
            visName: awayTeam.teamName,
            homeName: homeTeam.teamName,
            date,
            location: city,
            arena: venue,
            time,
            endTime: formattedEndTime,
            duration: duration,
            scheduleNote: "",
            notes: notes,
            attendance: attendance,
            leagueGame: isConference,
            regionGame: isRegional,
            neutralGame: isNeutral,
            niteGame: !isBefore7PM(startDateTime),
            postSeason: isPostSeason,
            forceHome: "N/A",
            tournament: "N/A",
            tournamentName: "N/A"
        };
    }

    /**
     * Retrieve value with key where parameter code matches some enumObj code
     * @param {Record<string,(BaseEnumType & {value?: string | number, points?: string | number})>} enumObj
     * @param {string | number} code
     * @param {string} key
     * @returns {*}
     */
    getEnumValue(enumObj, code, key = "name") {
        return enumObj[
            Object.keys(enumObj).find(
                (key) => `${enumObj[key].code}` === `${code}`
            )
        ]?.[key];
    }

    getRules(gameConfig) {
        const { singles: singleConfig, doubles: doubleConfig } = gameConfig;
        const dblScoringVal = this.getEnumValue(
            ScoringMode,
            doubleConfig.scoringMode,
            "value"
        );

        return {
            doublesOne:
                dblScoringVal === ScoringMode.BestOfSeries.value ? 1 : 0,
            doublesTwo:
                dblScoringVal === ScoringMode.OnePointPerMatch.value ? 1 : 0,
            indivOnly: 0,
            suspended: 0,
            format: 1,
            squash: 0,
            singles: {
                matchCount: singleConfig.numberOfMatches,
                gameType: this.getEnumValue(GameType, singleConfig.gameType),
                gamesPerSet: singleConfig.gamesPerSet,
                tiebreakPts: this.getEnumValue(
                    TiebreakType,
                    singleConfig.tieBreakType,
                    "points"
                ),
                scoringMode: this.getEnumValue(
                    ScoringMode,
                    singleConfig.scoringMode
                ),
                courtType: this.getEnumValue(CourtType, singleConfig.courtType)
            },
            doubles: {
                matchCount: doubleConfig.numberOfMatches,
                gameType: this.getEnumValue(GameType, doubleConfig.gameType),
                gamesPerSet: doubleConfig.gamesPerSet,
                tiebreakPts: this.getEnumValue(
                    TiebreakType,
                    doubleConfig.tieBreakType,
                    "points"
                ),
                scoringMode: this.getEnumValue(
                    ScoringMode,
                    doubleConfig.scoringMode
                ),
                courtType: this.getEnumValue(CourtType, doubleConfig.courtType)
            }
        };
    }
}
